import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AUDIT_MENUS, AUDIT_CATEGORIES } from '@cornerstone/shared';
import { api } from '../client/api';
import type { AuditHistoryParams, AuditHistoryRow, BoTenant } from '../client/api';
import { SearchInput, Button, Field } from '../components/ui';
import { PageContainer } from '../nav/PageContainer';
import { ModalShell } from '../components/menu';
import { PageHeader } from '../nav/Breadcrumb';
import { ManagementHistorySkeleton } from '../components/skeleton/layouts/ManagementHistorySkeleton';
import { ContentSkeleton } from '../components/skeleton/ContentSkeleton';
import { GroupActivityHistory, useHistoryAccess } from '../components/GroupActivityHistory';

// =============================================================================
// 관리 이력 (Management history) — FO Admin이 접근하는 활동 이력. 서버가 audit_log를
// 메뉴/구분/내용으로 파생해 반환한다(routes/auditLogs.ts). BO 운영자는 조직 차원이
// 추가로 보인다(조직 컬럼 + 조직 필터). 일반 FO는 자기 조직만·조직 항목 미노출.
// 엑셀(CSV) 다운로드 지원(전체/선택 기간). 기본 조회 1년, 최대 5년.
// =============================================================================

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function shiftYears(base: Date, years: number): Date {
  const d = new Date(base);
  d.setFullYear(d.getFullYear() + years);
  return d;
}

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const;
/** 2026-09-16 검수 P3 — 기본 20건. 10건이면 흐름이 끊기고, 50건이면 페이지네이션이 밀린다. */
const DEFAULT_PAGE_SIZE = 20;

// 2026-09-16 검수 S17 — 서버(routes/auditLogs.ts)가 표시명과 원본 식별자를 나눠
// 내려 준다. client/api의 AuditHistoryRow 타입은 다른 담당 영역이라 여기서만
// 넓혀 쓴다(그쪽 타입이 따라오면 이 별칭은 지운다).
type HistoryRowView = AuditHistoryRow;

/** Figma 일시 포맷 — YYYY. MM. DD HH:MM:SS */
function formatHistoryDateTime(iso: string): string {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}. ${p(d.getMonth() + 1)}. ${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

export function ManagementHistory(): JSX.Element {
  const { access, reload, revision } = useHistoryAccess();
  if (access.kind === 'legacy') return <LegacyManagementHistory key={revision} />;
  if (access.kind === 'group') return <GroupActivityHistory key={revision} scope={access.scope} reload={reload} />;
  return <PageContainer><PageHeader items={[{ label: '관리이력' }]} />
    {access.kind === 'loading' ? <ContentSkeleton label="이력 조회 권한을 확인하는 중…" variant="table" /> : <>
      <p role="alert" className="text-red">{access.kind === 'error' ? access.message : '관리 이력 조회 권한이 없습니다. 중앙 관리자 또는 담당 그룹 관리자 권한이 필요합니다.'}</p>
      <Button onClick={reload}>다시 불러오기</Button>
    </>}
  </PageContainer>;
}

function LegacyManagementHistory(): JSX.Element {
  const today = useMemo(() => new Date(), []);
  const defaultFrom = useMemo(() => isoDate(shiftYears(today, -1)), [today]);
  const defaultTo = useMemo(() => isoDate(today), [today]);
  const minFrom = useMemo(() => isoDate(shiftYears(today, -5)), [today]);

  const [menu, setMenu] = useState('');
  const [category, setCategory] = useState('');
  const [orgId, setOrgId] = useState('');
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [qDraft, setQDraft] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const [rows, setRows] = useState<HistoryRowView[]>([]);
  const [total, setTotal] = useState(0);
  const [isBo, setIsBo] = useState(false);
  const [tenants, setTenants] = useState<BoTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showExport, setShowExport] = useState(false);
  const loadVersion = useRef(0);

  const params = useMemo(
    (): AuditHistoryParams => ({
      ...(menu ? { menu } : {}),
      ...(category ? { category } : {}),
      ...(orgId ? { orgId } : {}),
      // BRICK-159 — 날짜 입력은 KST 기준이다. UTC 자정으로 보내면 하루 경계가
      // 9시간 어긋나 전날 09:00 이전/당일 09:00 이후 이력이 잘려 나간다.
      ...(from ? { from: `${from}T00:00:00.000+09:00` } : {}),
      ...(to ? { to: `${to}T23:59:59.999+09:00` } : {}),
      ...(q ? { q } : {}),
      page,
      pageSize,
    }),
    [menu, category, orgId, from, to, q, page, pageSize],
  );

  const load = useCallback(async () => {
    const version = ++loadVersion.current;
    setLoading(true);
    setError(null);
    setRows([]); setTotal(0); setShowExport(false);
    try {
      const res = await api.fetchAuditHistory(params);
      if (version !== loadVersion.current) return;
      setRows(res.items);
      setTotal(res.total);
      setIsBo(res.isBo);
    } catch (e) {
      if (version !== loadVersion.current) return;
      setRows([]); setTotal(0); setTenants([]); setIsBo(false);
      const msg = e instanceof Error ? e.message : String(e);
      // 403 = 권한 부족 — raw JSON 대신 사용자용 안내.
      setError(
        msg.includes('403')
          ? '관리 이력 조회 권한이 없습니다. 관리자 이상 권한이 필요합니다.'
          : msg,
      );
    } finally {
      if (version === loadVersion.current) { setLoading(false); setHasLoaded(true); }
    }
  }, [params]);

  useEffect(() => {
    void load();
    return () => { loadVersion.current++; };
  }, [load]);

  // BO 운영자면 조직 필터용 테넌트 목록을 한 번 불러온다.
  useEffect(() => {
    if (!isBo) return;
    let cancelled = false;
    api
      .boListTenants()
      .then((list) => {
        if (!cancelled) setTenants(list);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isBo]);

  const totalPages = Math.max(Math.ceil(total / pageSize), 1);

  function downloadCsv(range: 'all' | 'selected'): void {
    const url = api.auditHistoryCsvUrl(params, range);
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setShowExport(false);
  }

  // 컬럼 수(빈 상태 colSpan) — 조직명(BO) + 아이디·메뉴·구분·내용·일시 + 복사.
  const cols = isBo ? 6 : 5;

  if (loading && !hasLoaded) {
    return <ManagementHistorySkeleton isBo={isBo} />;
  }

  return (
    <PageContainer variant="content" fluid column={false} className="text-text">
      <PageHeader items={[{ label: '관리이력' }]} className="mb-6" />

      {/* 필터 — 디바이스 관리와 동일한 맨몸 셀렉트 스타일 (Panel 래퍼 없음). */}
      <div className="mb-4 flex flex-wrap items-end gap-4 max-md:grid max-md:grid-cols-2 max-md:items-start max-md:gap-2 max-md:[&>*]:min-w-0 max-md:[&_select]:h-[42px] max-md:[&_input]:h-[42px] max-md:[&_label>span.font-mono]:!mb-1 max-md:[&_label>span.font-mono]:leading-[18px] max-md:[&_input]:w-full max-md:[&_select]:w-full">
        {isBo && (
          <label className="flex flex-col gap-1">
            <span className="text-[12px] text-text-dim">조직</span>
            <select
              className="w-[180px] px-2.5 py-2 bg-panel-2 border border-line rounded text-[14px] text-text"
              value={orgId}
              onChange={(e) => {
                setOrgId(e.target.value);
                setPage(1);
              }}
            >
              <option value="">전체</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-text-dim">메뉴</span>
          <select
            className="w-[180px] px-2.5 py-2 bg-panel-2 border border-line rounded text-[14px] text-text"
            value={menu}
            onChange={(e) => {
              setMenu(e.target.value);
              setPage(1);
            }}
          >
            <option value="">전체</option>
            {AUDIT_MENUS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-text-dim">구분</span>
          <select
            className="w-[180px] px-2.5 py-2 bg-panel-2 border border-line rounded text-[14px] text-text"
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setPage(1);
            }}
          >
            <option value="">전체</option>
            {AUDIT_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        {/* 다른 필터(셀렉트 180px·14px)와 같은 크기로 — 시작일·종료일이 서로·주변과 다르게 보이던 것(2026-09-16 대교). */}
        <Field
          label="시작일"
          type="date"
          className="w-[180px] max-md:w-full"
          inputClassName="h-[38px] !px-2.5 !py-0 !text-[14px] !font-sans"
          min={minFrom}
          max={to}
          value={from}
          onChange={(e) => {
            setFrom(e.target.value);
            setPage(1);
          }}
        />
        <Field
          label="종료일"
          type="date"
          className="w-[180px] max-md:w-full"
          inputClassName="h-[38px] !px-2.5 !py-0 !text-[14px] !font-sans"
          min={from}
          max={defaultTo}
          value={to}
          onChange={(e) => {
            setTo(e.target.value);
            setPage(1);
          }}
        />
        <div className="flex flex-col gap-1 max-md:col-span-2">
          <span className="text-[12px] text-text-dim">
            {isBo ? '조직명·아이디 검색' : '아이디 검색'}
          </span>
          <SearchInput
            className="w-[220px] max-md:w-full"
            inputClassName="h-[38px] bg-panel"
            placeholder={isBo ? '조직명, 아이디 검색' : '아이디 검색'}
            value={qDraft}
            onChange={setQDraft}
            onSearch={() => {
              setQ(qDraft.trim());
              setPage(1);
            }}
          />
        </div>
        <div className="ml-auto flex flex-col gap-1 max-md:col-span-2 max-md:ml-0">
          <span className="text-[12px] text-transparent max-md:hidden">.</span>
          <Button variant="primary" disabled={loading || !!error} onClick={() => setShowExport(true)}>
            엑셀 다운로드
          </Button>
        </div>
      </div>

      {error && (
        <div
          role="alert"
          className="mb-4 rounded border border-red/50 bg-red/15 px-3 py-2 text-[15px] text-red"
        >
          {error}
        </div>
      )}

      <div className="mb-3 text-[14px] text-text-dim">
        총 {total}건{loading ? ' · 로딩 중…' : ''}
      </div>

      {/* 표 — 디바이스 관리 목록과 동일 스타일 (border 래퍼 + bg-panel-2 헤더 + border-t 행). */}
      <div className="border border-line rounded-md overflow-x-auto">
        <table data-mobile-cards className="w-full text-[14px]">
          <thead>
            <tr className="bg-panel-2 text-left text-text-dim">
              {isBo && <th className="px-3 py-2.5 font-medium">조직명</th>}
              <th className="px-3 py-2.5 font-medium">아이디</th>
              <th className="px-3 py-2.5 font-medium">메뉴</th>
              <th className="px-3 py-2.5 font-medium">구분</th>
              <th className="px-3 py-2.5 font-medium">내용</th>
              <th className="px-3 py-2.5 font-medium whitespace-nowrap">일시</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={cols} className="px-3 py-10 text-center text-text-dim">
                  {error ? '이력을 표시할 수 없습니다.' : loading ? '불러오는 중…' : '표시할 이력이 없습니다.'}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="border-t border-line hover:bg-panel-hover">
                  {isBo && (
                    <td className="max-w-[220px] truncate px-3 py-2.5" title={row.orgName}>
                      {row.orgName}
                    </td>
                  )}
                  {/* 표시·검색·CSV는 로그인 ID. 원본 감사 식별자는 별도로 보존한다. */}
                  <td className="max-w-[180px] truncate px-3 py-2.5" title={`원본 수행자: ${row.actorId}`}>
                    {row.actorName ?? row.actorId}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{row.menu}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{row.category}</td>
                  <td data-card="full" className="max-w-[420px] truncate px-3 py-2.5 max-md:whitespace-normal" title={row.content}>
                    {row.content}
                  </td>
                  {/* 일시는 날짜·시간만 표시하고 수행자는 아이디 열에서 확인한다. */}
                  <td className="px-3 py-2.5 whitespace-nowrap text-text-dim">
                    {formatHistoryDateTime(row.createdAt)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 어시스턴트 FAB(높이 48 + 여백 16)가 페이지네이션을 덮지 않도록 아래를 비운다(S4). */}
      <div className="mt-4 flex items-center justify-end gap-3 pb-24">
        <select
          className="h-[34px] rounded border border-line bg-panel px-2 text-[13px] text-text"
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(1);
          }}
        >
          {PAGE_SIZE_OPTIONS.map((n) => (
            <option key={n} value={n}>
              {n} / page
            </option>
          ))}
        </select>
        <div className="flex items-center gap-1">
          <Button disabled={page <= 1} onClick={() => setPage(1)}>
            «
          </Button>
          <Button disabled={page <= 1} onClick={() => setPage((p) => Math.max(p - 1, 1))}>
            ‹
          </Button>
          <span className="px-2 text-[14px] text-text-dim">
            {page} / {totalPages}
          </span>
          <Button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
          >
            ›
          </Button>
          <Button disabled={page >= totalPages} onClick={() => setPage(totalPages)}>
            »
          </Button>
        </div>
      </div>

      {showExport && (
        <ExportModal
          from={from}
          to={to}
          onClose={() => setShowExport(false)}
          onConfirm={downloadCsv}
        />
      )}
    </PageContainer>
  );
}

function ExportModal({
  from,
  to,
  onClose,
  onConfirm,
}: {
  from: string;
  to: string;
  onClose: () => void;
  onConfirm: (range: 'all' | 'selected') => void;
}): JSX.Element {
  const [range, setRange] = useState<'all' | 'selected'>('all');
  // 2026-09-16 검수 P2 — 자체 딤/헤더/푸터를 쓰지 않고 공용 ModalShell로. ModalShell이
  // role="dialog"·aria-modal·포커스 트랩을 스스로 달기 때문에 여기서 또 달면 dialog가
  // 두 겹이 된다.
  return (
    <ModalShell
      title="엑셀 다운로드"
      onClose={onClose}
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>취소</Button>
          <Button variant="primary" onClick={() => onConfirm(range)}>
            확인
          </Button>
        </div>
      }
    >
      <p className="mb-4 text-[14px] text-text-dim">데이터를 다운로드할 기간을 선택해 주세요.</p>
      <label className="mb-2 flex items-center gap-2 text-[15px]">
        <input
          type="radio"
          checked={range === 'all'}
          onChange={() => setRange('all')}
        />
        전체 기간
      </label>
      <label className="flex items-center gap-2 text-[15px]">
        <input
          type="radio"
          checked={range === 'selected'}
          onChange={() => setRange('selected')}
        />
        선택한 기간
      </label>
      <div className="mt-2 rounded border border-line bg-panel-2 px-3 py-2 text-[14px] text-text-dim">
        {from} → {to}
      </div>
    </ModalShell>
  );
}
